/**
 * 📍 AT STORE TAB - Mode Mozart/Jobs/Tesla
 * 
 * Les gens présents à l'épicerie en ce moment
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, ShoppingBag, Heart, Clock, Users, Sparkles } from 'lucide-react';
import { Button } from '../../ui/button';
import { api } from '../../../services/api';
import { toast } from 'sonner';

function AtStoreTab({ customerId, isDarkMode, profile, onUpdateProfile }) {
  const [atStoreUsers, setAtStoreUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAtStore, setIsAtStore] = useState(profile?.atStore || false);

  useEffect(() => {
    loadAtStoreUsers();
  }, [customerId]);

  const loadAtStoreUsers = async () => {
    try {
      setLoading(true);
      const response = await api.getHeartbeatsProfiles(customerId);
      const profiles = response?.profiles || response || [];
      const atStore = profiles.filter(p => p.atStore && p.customerId !== customerId);
      setAtStoreUsers(atStore);
    } catch (error) {
      console.error('Erreur chargement utilisateurs en magasin:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAtStore = async () => {
    try {
      const newStatus = !isAtStore;
      await api.updateHeartbeatsProfile(customerId, { atStore: newStatus });
      setIsAtStore(newStatus);
      onUpdateProfile?.({ atStore: newStatus });
      
      if (newStatus) {
        toast.success('📍 Tu es maintenant visible à l\'épicerie !');
        loadAtStoreUsers();
      } else {
        toast.success('👋 Tu as quitté l\'épicerie');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toggle présence */}
      <motion.div 
        className={`p-4 rounded-xl ${
          isAtStore 
            ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30'
            : isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
        }`}
        whileHover={{ scale: 1.01 }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isAtStore 
                ? 'bg-green-500/30 text-green-500' 
                : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
            }`}>
              <ShoppingBag className="w-6 h-6" />
            </div>
            <div>
              <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {isAtStore ? '📍 Tu es à l\'épicerie !' : 'Tu n\'es pas à l\'épicerie'}
              </h4>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {isAtStore ? 'Les autres peuvent te voir' : 'Signale ta présence'}
              </p>
            </div>
          </div>
          <Button
            onClick={toggleAtStore}
            variant={isAtStore ? 'outline' : 'default'}
            className={isAtStore ? '' : 'bg-gradient-to-r from-purple-500 to-pink-500'}
          >
            {isAtStore ? 'Quitter' : 'J\'y suis !'}
          </Button>
        </div>
      </motion.div>

      {/* Liste des présents */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Users className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
          <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Qui est là ? ({atStoreUsers.length})
          </h3>
        </div>

        {atStoreUsers.length === 0 ? (
          <div className={`text-center py-8 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
            <ShoppingBag className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
              Personne n'est à l'épicerie pour le moment
            </p>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Sois le/la premier(e) ! 🌟
            </p>
          </div>
        ) : (
          <div className="grid gap-3">
            {atStoreUsers.map((user, idx) => (
              <motion.div
                key={user.customerId || idx}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: idx * 0.1 }}
                className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
              >
                <div className="flex items-center gap-4">
                  {/* Avatar avec indicateur "en ligne" */}
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center overflow-hidden">
                      {user.avatarImage ? (
                        <img src={user.avatarImage} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-2xl">{user.avatar || '😊'}</span>
                      )}
                    </div>
                    <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-800"></div>
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {user.displayName}
                    </h4>
                    <p className={`text-xs flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <Clock className="w-3 h-3" />
                      À l'épicerie maintenant
                    </p>
                    {user.bio && (
                      <p className={`text-xs mt-1 truncate max-w-[200px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {user.bio}
                      </p>
                    )}
                  </div>

                  {/* Action */}
                  <Button
                    size="sm"
                    onClick={() => {
                      api.sendCrush(customerId, user.customerId)
                        .then(() => toast.success('💜 Crush envoyé !'))
                        .catch(() => toast.error('Erreur'));
                    }}
                    className="bg-gradient-to-r from-purple-500 to-pink-500"
                  >
                    <Heart className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-purple-900/20' : 'bg-purple-50'}`}>
        <p className={`text-xs text-center ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
          <Sparkles className="w-3 h-3 inline mr-1" />
          Signale ta présence pour rencontrer des gens en vrai !
        </p>
      </div>
    </div>
  );
}

export default AtStoreTab;
